import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useFormDraft } from "../lib/formDraft";
import {
  Button, Input, FormField, Dialog, DialogCancel,
  Spinner, Empty, PageHeader, SearchInput,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui";
import { Plus, Truck } from "lucide-react";
import { toast } from "sonner";

function emptyForm() {
  return { nombre: "", ruc: "", contacto: "", telefono: "", email: "", categoria_suministro: "" };
}

export default function Proveedores() {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [open,    setOpen]    = useState(false);
  const [edit,    setEdit]    = useState(null);
  const [form,    setForm]    = useState(emptyForm());
  const [saving,  setSaving]  = useState(false);
  const draft = useFormDraft("proveedor", form, () => setForm(emptyForm()));

  const load = useCallback(async () => {
    const r = await api.get("/proveedores");
    setList(r.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function openEdit(p) {
    setEdit(p);
    setForm({
      nombre: p.nombre, ruc: p.ruc ?? "", contacto: p.contacto ?? "",
      telefono: p.telefono ?? "", email: p.email ?? "", categoria_suministro: p.categoria_suministro ?? "",
    });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...form,
      ruc: form.ruc || null, contacto: form.contacto || null,
      email: form.email || null, categoria_suministro: form.categoria_suministro || null,
    };
    try {
      if (edit) {
        await api.put(`/proveedores/${edit.id}`, body);
        toast.success("Proveedor actualizado");
      } else {
        await api.post("/proveedores", body);
        toast.success("Proveedor creado");
        draft.clear();
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  const filtered = list.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.nombre?.toLowerCase().includes(q) || p.ruc?.toLowerCase()?.includes(q);
  });

  if (loading) return <div className="flex items-center justify-center h-full"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Proveedores" sub={`${list.length} registros`}>
        <Button onClick={() => { setEdit(null); setForm({ ...emptyForm(), ...draft.load() }); setOpen(true); }}>
          <Plus className="w-4 h-4" /> Nuevo proveedor
        </Button>
      </PageHeader>

      <SearchInput value={search} onChange={setSearch} placeholder="Buscar…" className="w-full sm:w-64 mb-4" />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>RUC</TableHead>
            <TableHead>Contacto</TableHead>
            <TableHead>Teléfono</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Categoría</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={7}><Empty message="Sin proveedores" icon={Truck} /></TableCell></TableRow>
          )}
          {filtered.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="font-medium text-sm">{p.nombre}</TableCell>
              <TableCell className="font-mono text-xs">{p.ruc ?? "—"}</TableCell>
              <TableCell className="text-sm">{p.contacto ?? "—"}</TableCell>
              <TableCell className="text-sm">{p.telefono ?? "—"}</TableCell>
              <TableCell className="text-sm">{p.email ?? "—"}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{p.categoria_suministro ?? "—"}</TableCell>
              <TableCell>
                <button onClick={() => openEdit(p)} className="text-xs text-primary hover:underline">Editar</button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} values={form} draft={edit ? undefined : draft} title={edit ? "Editar proveedor" : "Nuevo proveedor"} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Nombre *" className="col-span-full">
              <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required />
            </FormField>
            <FormField label="RUC">
              <Input value={form.ruc} onChange={(e) => s("ruc", e.target.value)} />
            </FormField>
            <FormField label="Contacto">
              <Input value={form.contacto} onChange={(e) => s("contacto", e.target.value)} />
            </FormField>
            <FormField label="Teléfono">
              <Input value={form.telefono} onChange={(e) => s("telefono", e.target.value)} />
            </FormField>
            <FormField label="Email">
              <Input type="email" value={form.email} onChange={(e) => s("email", e.target.value)} />
            </FormField>
            <FormField label="Categoría de suministro" className="col-span-full">
              <Input value={form.categoria_suministro} onChange={(e) => s("categoria_suministro", e.target.value)} placeholder="Ej: Pintura, Repuestos mecánicos…" />
            </FormField>
          </div>
          <div className="flex justify-end gap-2">
            <DialogCancel />
            <Button type="submit" loading={saving}>Guardar</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
